import { APPROVED_EVENTS, EXCEPTION_PARTS, PERSON_PROPERTIES } from "./events";
import { apiErrorStatus } from "./redact-server-detail";

type Properties = Record<string, unknown>;

/** An event as the SDK hands it to the send hook, reduced to what is filtered. */
interface CapturedEvent {
  event: string;
  properties: Properties;
  $set?: Properties;
  $set_once?: Properties;
}

const PERSON_CARRIERS: ReadonlySet<string> = new Set(["$set", "$set_once"]);

function isPlainObject(value: unknown): value is Properties {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pick(properties: Properties, approved: ReadonlySet<string>) {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => approved.has(key)),
  );
}

/** `undefined` when nothing approved is left, so an empty set is not sent. */
function approvedPersonProperties(carrier: unknown): Properties | undefined {
  if (!isPlainObject(carrier)) return undefined;
  const kept = pick(carrier, PERSON_PROPERTIES);
  return Object.keys(kept).length > 0 ? kept : undefined;
}

/**
 * An error keeps what names code: its type, its stack and how it was caught.
 * Its message goes, because a thrown message can quote whatever the code held,
 * and here that can be a draft. All that is kept of a message is an API
 * failure's status line, which says nothing about content.
 */
function withoutMessages(list: unknown[]): Properties[] {
  return list.filter(isPlainObject).map((entry) => ({
    ...pick(entry, EXCEPTION_PARTS),
    value: apiErrorStatus(entry.value) ?? "",
  }));
}

function approvedProperties(
  event: string,
  properties: Properties,
  approved: ReadonlySet<string>,
): Properties {
  const kept = pick(properties, approved);
  for (const carrier of PERSON_CARRIERS) {
    const persons = approvedPersonProperties(properties[carrier]);
    if (persons) kept[carrier] = persons;
  }
  if (event === "$exception" && "$exception_list" in kept) {
    const list: unknown = kept.$exception_list;
    kept.$exception_list = Array.isArray(list) ? withoutMessages(list) : [];
  }
  return kept;
}

/**
 * Holds an outgoing event to the approved list in `events.ts`: an event not
 * on it is dropped, and an approved one loses every property not named for
 * it. Person properties are held to their own list, wherever the SDK puts
 * them: beside the event for an identify, inside it for a `$set`.
 *
 * Runs last in the send hook, so nothing added after it can leave unlisted.
 */
export function keepApproved<T extends CapturedEvent>(event: T): T | null {
  const approved = APPROVED_EVENTS.get(event.event);
  if (!approved) return null;

  return {
    ...event,
    properties: approvedProperties(event.event, event.properties, approved),
    ...(event.$set && { $set: approvedPersonProperties(event.$set) }),
    ...(event.$set_once && {
      $set_once: approvedPersonProperties(event.$set_once),
    }),
  };
}
